import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { GatsbyImage } from 'gatsby-plugin-image';

import { colors, media, Text } from '../ui-components';

const propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.object,
      name: PropTypes.string
    })
  ),
  className: PropTypes.string,
  condensed: PropTypes.bool
};

/**
 * Component that renders the list of authors. If condensed, it will stack the thumbnails and
 * only display one name, otherwise it will display all thumbnails and names.
 *
 * @component Authors
 * 
 * @param {*} props
 * 
 * @prop {array} authors
 * @prop {string} className
 * @prop {boolean} condensed
 *
 * @returns {JSX} authors component
 */
const Authors = props => {
  const { authors, className, condensed } = props;
  /**
   * Renders the author names. If condensed, displays only the first name and the number of
   * additional authors. Otherwise, displays all names with appropriate punctuation.
   *
   * @function renderAuthorNames
   * 
   * @returns {JSX.Element} the list of author names
   */
  const renderAuthorNames = () => {
    let authorNames;

    if (condensed) {
      authorNames = authors[0]?.fields ? authors[0]?.fields?.name : authors[0]?.name;

      if (authors.length > 1) {
        authorNames += `, +${authors.length - 1} more`;
      }
    } else {
      authorNames = authors.map((author, index) => {
        let prefix = '';
        if (authors.length > 1 && index === authors.length - 1) {
          prefix += ' and';
        }

        return `${prefix} ${author?.fields ? author?.fields?.name : author.name}`;
      });

      if (authorNames.length > 2) {
        authorNames = authorNames.join(', ');
      }
    }

    return <AuthorNameText size="medium">{authorNames}</AuthorNameText>;
  };

  /**
   * Renders the author avatars. If condensed is true, it will stack a max of 3 avatars. Otherwise,
   * all avatars will be displayed.
   *
   * @function renderAuthorAvatars
   * 
   * @returns {JSX.Element} the author avatars
   */
  const renderAuthorAvatars = () => {

    return (
      <AvatarsWrapper condensed={+condensed} numberOfAuthors={authors.length > 3 ? 3 : authors.length}>
        {authors.map((author, index) => {
          const avatarContent = author?.avatar?.url ? (
            <AvatarImg condensed={+condensed} alt={author.name} src={author.avatar.url} />
          ) : 
          author.avatar && author.avatar.fluid ? (
            <AvatarGatsbyImg condensed={+condensed} image={author.avatar.fluid} alt={author.name} />
          ) : 
          author?.fields?.avatar ? (
            <AvatarImg condensed={+condensed} src={author.fields.avatar?.fields.file.url} alt={author.fields?.name} />
          ) : null;

          return condensed && index > 2 ? null : (
            <ImageWrapper condensed={+condensed} index={index} key={author.name}>
              {avatarContent}
            </ImageWrapper>
          );
        })}
      </AvatarsWrapper>
    );
  };

  return authors ? (
    <AuthorsWrapper className={className}>
      {renderAuthorAvatars()}
      {renderAuthorNames()}
    </AuthorsWrapper>
  ) : null;
};

Authors.propTypes = propTypes;

const AuthorsWrapper = styled.div`
  margin-top: 24px;
  display: flex;

  @media only screen and (max-width: ${media.smallTop}) {
    margin-top: 12px;
  }
`;

const AvatarsWrapper = styled.div`
  position: relative;
  align-self: center;
  height: 40px;
  width: ${({ condensed, numberOfAuthors }) => (condensed ? `${40 + 8 * (numberOfAuthors - 1)}px` : 'auto')};
  margin-right: ${({ condensed }) => (condensed ? 8 : 20)}px;
`;

const ImageWrapper = styled.div`
  display: inline-block;
  ${({ condensed, index }) =>
    condensed
      ? `position: absolute;
         left: ${index * 8}px;
         z-index: ${3 - index};
         `
      : ''}
`;

const AvatarBaseStyles = css`
  width: 40px;
  height: 40px;
  margin-right: 8px;
  margin-bottom: 0;
  border-radius: 100%;
  ${({ condensed }) => (condensed ? `border: 2px solid ${colors.pureWhite}; ` : '')}

  img {
    margin-bottom: 0;
  }
`;

const AvatarGatsbyImg = styled(props => <GatsbyImage {...props} />)`
  ${AvatarBaseStyles}
`;

const AvatarImg = styled(props => <img {...props} />)`
  ${AvatarBaseStyles}
`;

const AuthorNameText = styled(props => <Text {...props} />)`
  align-self: center;
`;

export default Authors;
